<script setup lang="tsx">
import type { URLReference, URL, URLReferenceChanges } from '@/models/api-generated'
import IconButton from '../shared/IconButton.vue'
import Modal from '../shared/Modal.vue'
import DocumentFolders from '../document/DocumentFolders.vue'
import { computed, ref, watch } from 'vue'
import { hasPermission } from '@/services/utils'
import { PermDocumentAdd, PermDocumentRead } from '@/models/enums-generated'
import { truncate } from '@/services/utils'

const model = defineModel<URLReferenceChanges[]>({ required: true })
const showFolderModal = ref(false)
const selectedURLRef = ref<URL>()
const currentRefIndex = ref<number>()
const pendingSelection = ref<URL>()
const emit = defineEmits<{ (e: 'removed', ref: URLReference): void }>()

const canView = computed(() => hasPermission(PermDocumentRead))
const canAddURL = computed(() => hasPermission(PermDocumentAdd))

const props = defineProps<{
	canEdit?: boolean
	ownerId?: number
	ownerType: string
}>()

const emptyUrlRef = (): URLReference => ({
	id: 0,
	title: '',
	ownerId: props.ownerId ?? 0,
	ownerType: props.ownerType,
	urlId: 0,
	url: {
		id: 0,
		path: '/',
		url: '',
		referenceCount: 0,
		author: sessionMap.user,
		instance: undefined as any,
	},
})

function removeRef(index: number) {
	const entry = model.value[index]
	model.value.splice(index, 1)
	if (entry) {
		const toEmit = entry.oldReferences?.urlId ? entry.oldReferences : entry.changedReferences
		emit('removed', toEmit)
	}
}

function addRef() {
	model.value.push({
		oldReferences: emptyUrlRef(),
		changedReferences: emptyUrlRef(),
	})
}

function selectUrl() {
	selectedURLRef.value = pendingSelection.value
	showFolderModal.value = false
}

function openURLModal(index: number | undefined, url?: URL) {
	currentRefIndex.value = index
	showFolderModal.value = true
	if (url) pendingSelection.value = url
}

watch(selectedURLRef, (url) => {
	if (!url || currentRefIndex.value === undefined) return
	const entry = model.value[currentRefIndex.value]
	entry.changedReferences.urlId = url.id
	entry.changedReferences.url = url
	selectedURLRef.value = undefined
	showFolderModal.value = false
	currentRefIndex.value = undefined
})
</script>

<template>
	<div class="mb-3">
		<div class="d-flex align-items-center mb-2">
			<strong>{{ $t('reference.url') }}</strong>
		</div>
		<template v-if="!model?.length">
			<div>-</div>
		</template>
		<template v-else>
			<div v-for="(ref, index) in model" :key="index" class="mb-2">
				<div class="d-flex flex-column gap-1">
					<template v-if="props.canEdit">
						<div class="d-flex gap-2 align-items-start w-100">
							<input
								:data-test="ownerType + '-url-select-' + ownerId"
								type="text"
								class="form-control"
								:placeholder="$t('reference.selectUrl')"
								:value="ref.changedReferences.url?.url || ''"
								readonly
								@click="openURLModal(index)"
							/>
							<IconButton
								:data-test="ownerType + '-delete-reference-' + ownerId"
								class="bi bi-trash"
								:title="$t('misc.delete')"
								@click="removeRef(index)"
								size="fs-5"
							/>
						</div>
						<input
							:data-test="ownerType + '-reference-title-' + ownerId"
							type="text"
							class="form-control"
							:placeholder="$t('reference.title')"
							v-model="ref.changedReferences.title"
						/>
					</template>
					<template v-else>
						<div class="d-flex align-items-start gap-2" @click="openURLModal(undefined, ref.changedReferences.url)">
							<i class="bi bi-url-earmark-text text-secondary" v-one-tooltip="ref.changedReferences.url.url"></i>
							<span>{{ ref.changedReferences.title || truncate(ref.changedReferences.url?.url) }}</span>
						</div>
					</template>
				</div>
				<hr v-if="index !== model.length - 1" class="my-2 border-top border-secondary opacity-25" />
			</div>
		</template>
		<button
			v-if="canAddURL && props.canEdit && canView"
			:data-test="ownerType + '-url-reference-' + ownerId"
			type="button"
			class="btn btn-outline-primary btn-sm"
			@click="addRef()"
		>
			<i class="bi bi-plus-circle me-1"></i> {{ $t('reference.add') }}
		</button>
		<Modal v-model="showFolderModal" class="modal-xl url-reference-modal">
			<template #header>
				<div class="row">
					<div class="ellipsis col-11">
						{{ $t('document.selectUrl') }}
					</div>
				</div>
			</template>
			<template #body>
				<DocumentFolders
					@url-selected="pendingSelection = $event"
					:show-select="currentRefIndex !== undefined"
					:preselect-url="pendingSelection"
					selection-type="url"
				/>
			</template>
			<template #footer>
				<button class="btn btn-secondary" @click="showFolderModal = false">
					{{ $t('misc.cancel') }}
				</button>
				<button class="btn btn-primary" :disabled="!pendingSelection" @click="selectUrl">
					{{ $t('misc.confirm') }}
				</button>
			</template>
		</Modal>
	</div>
</template>

<style>
.modal.fade.modal-xl.url-reference-modal.show {
	z-index: 9999;
}
</style>

<style scoped>
:deep(.modal-dialog.modal-xl) {
	max-width: 75vw;
}
</style>
